import React, {
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { useAppSelector } from "../../api/hooks";
import "./SpreadPage.css";
import { CardSlot } from "../../components/CardSlot/CardSlot";
import type { ICard } from "../../tarot/cards/types";
import { getSpreadLayoutKey } from "../../tarot/layout/spreadLayouts";
import { buildSpreadReading } from "../../tarot/reading/buildSpreadReading";
import { drawCards } from "../../tarot/reading/drawCards";
import { getReadingProfile } from "../../tarot/reading/profiles";
import type { ReadingTopic } from "../../tarot/reading/types";

interface SpreadDrawState {
  spreadId: string;
  cards: ICard[];
}

const READING_TOPICS: Array<{
  value: ReadingTopic;
  label: string;
}> = [
  {
    value: "general",
    label: "Общая",
  },
  {
    value: "personality",
    label: "Личность",
  },
  {
    value: "relationships",
    label: "Отношения",
  },
  {
    value: "work",
    label: "Работа",
  },
  {
    value: "spirituality",
    label: "Духовность",
  },
];

export const SpreadPage: React.FC = () => {
  const { spreadId } = useParams<{
    spreadId: string;
  }>();

  const spreads = useAppSelector(
    (state) => state.spreads.items
  );

  const {
    majorArcanes,
    minorArcanes,
  } = useAppSelector(
    (state) => state.cards
  );

  const [topic, setTopic] =
    useState<ReadingTopic>("general");

  const [drawState, setDrawState] =
    useState<SpreadDrawState | null>(null);

  const spread = spreads.find(
    (item) => item.id === spreadId
  );

  const allCards = useMemo(
    () => [
      ...majorArcanes.cards,
      ...minorArcanes.cards,
    ],
    [
      majorArcanes.cards,
      minorArcanes.cards,
    ]
  );

  const reading = useMemo(() => {
    if (
      !spread ||
      !drawState ||
      drawState.spreadId !== spread.id
    ) {
      return [];
    }

    const profile =
      getReadingProfile(spread.id);

    return buildSpreadReading({
      spread,
      cards: drawState.cards,
      profile,
      context: {
        topic,
      },
    });
  }, [
    drawState,
    spread,
    topic,
  ]);

  if (!spreadId) {
    return (
      <div className="spread-page">
        <div className="spread-warning">
          В URL нет spreadId расклада
        </div>
      </div>
    );
  }

  if (!spread) {
    return (
      <div className="spread-page">
        <div className="spread-warning">
          Расклад с spreadId «{spreadId}» не найден
        </div>
      </div>
    );
  }

  const layoutKey =
    getSpreadLayoutKey(spread.id);

  const hasDraw =
    drawState?.spreadId === spread.id;

  const startSpread = () => {
    const cards = drawCards(
      allCards,
      spread.cardsCount
    );

    setDrawState({
      spreadId: spread.id,
      cards,
    });
  };

  return (
    <div className="spread-page">
      <header className="spread-page__intro">
        <div className="spread-page__meta">
          <span>
            {spread.cardsCount} карт
          </span>

          <span>
            {spread.positions.length} позиций
          </span>
        </div>

        <h1 className="spread-page__title">
          {spread.name}
        </h1>

        {spread.altNames?.length ? (
          <div className="spread-page__alt">
            {spread.altNames.join(" / ")}
          </div>
        ) : null}

        <p className="spread-page__purpose">
          {spread.purpose}
        </p>
      </header>

      <section
        className="spread-controls"
        aria-label="Настройки расклада"
      >
        <div className="spread-controls__topic">
          <div className="spread-controls__heading">
            <div>
              <div className="spread-controls__label">
                Тема интерпретации
              </div>

              <div className="spread-controls__hint">
                Тема меняет трактовку, но не выпавшие карты.
              </div>
            </div>
          </div>

          <div
            className="spread-topic-options"
            role="group"
            aria-label="Тема интерпретации"
          >
            {READING_TOPICS.map(
              ({ value, label }) => (
                <button
                  key={value}
                  className={
                    topic === value
                      ? "spread-topic-button spread-topic-button--active"
                      : "spread-topic-button"
                  }
                  type="button"
                  aria-pressed={
                    topic === value
                  }
                  onClick={() =>
                    setTopic(value)
                  }
                >
                  {label}
                </button>
              )
            )}
          </div>
        </div>

        <button
          className="spread-action"
          type="button"
          onClick={startSpread}
        >
          <span>
            {hasDraw
              ? "Разложить заново"
              : "Разложить карты"}
          </span>

          <span
            className="spread-action__icon"
            aria-hidden="true"
          >
            ✦
          </span>
        </button>
      </section>

      <div
        className={
          hasDraw
            ? "spread-status spread-status--ready"
            : "spread-status"
        }
      >
        <span
          className="spread-status__marker"
          aria-hidden="true"
        />

        <span>
          {hasDraw
            ? "Расклад готов. Можно менять тему интерпретации без нового выбора карт."
            : "Карты ещё не выбраны. Позиции расклада показаны ниже."}
        </span>
      </div>

      <div
        className={
          `spread-board ` +
          `spread-board--${layoutKey}`
        }
      >
        {spread.positions.map(
          (position, index) => (
            <div
              className="spread-position"
              data-position-id={
                position.id
              }
              key={position.id}
            >
              <CardSlot
                index={index + 1}
                title={position.title}
                subtitle={position.meaning}
                reading={
                  reading[index] ?? null
                }
              />
            </div>
          )
        )}
      </div>
    </div>
  );
};

